import type { FieldProps } from '@seek/forms-ui/lib/Field/Field';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Checkbox,
  IconInfo,
  Notice,
  Stack,
  Text,
  TooltipRenderer,
} from 'braid-design-system';

import permissionTranslations from 'src/App/Components/Permissions/.vocab';
import translations from 'src/App/Components/UserFormProvider/.vocab';

import type { UserFormValues } from '../userFormFields';

interface FormPermissionsProps {
  accountHasPremiumTalentSearch: boolean;
  canCreatePosition: FieldProps<UserFormValues, 'canCreatePosition', any, any>;
  disableSelectPermissions: boolean;
  hasAllPositionAccess: FieldProps<
    UserFormValues,
    'hasAllPositionAccess',
    any,
    any
  >;
  hasAnalytics: FieldProps<UserFormValues, 'hasAnalytics', any, any>;
  hasPremiumTalentSearch: FieldProps<
    UserFormValues,
    'hasPremiumTalentSearch',
    any,
    any
  >;
}

export const FormPermissions = ({
  accountHasPremiumTalentSearch,
  canCreatePosition,
  hasAllPositionAccess,
  hasAnalytics,
  hasPremiumTalentSearch,
  disableSelectPermissions,
}: FormPermissionsProps) => {
  const { t } = useTranslations(translations);
  const { t: permissionTrans } = useTranslations(permissionTranslations);

  return (
    <Stack space="small">
      <Text weight="strong">{t('Permissions')}</Text>
      <Checkbox
        id="canCreatePosition"
        checked={canCreatePosition.value}
        label={permissionTrans('Job posting')}
        onChange={canCreatePosition.onChange}
        description={
          <Box display="flex">
            <Box paddingRight="xsmall">
              {permissionTrans('canCreatePosition permission description')}
            </Box>
            <TooltipRenderer
              id="jobPostingInfo"
              tooltip={<Text>{t('Job posting information message')}</Text>}
            >
              {({ triggerProps }) => (
                <Box aria-label="Info" {...triggerProps}>
                  <IconInfo />
                </Box>
              )}
            </TooltipRenderer>
          </Box>
        }
        disabled={disableSelectPermissions}
      />
      <Checkbox
        id="hasAllPositionAccess"
        checked={hasAllPositionAccess.value}
        label={permissionTrans('All jobs access')}
        onChange={hasAllPositionAccess.onChange}
        description={permissionTrans(
          'hasAllPositionAccess permission description',
        )}
        disabled={disableSelectPermissions}
      />
      {!hasAllPositionAccess.value && (
        <Box paddingLeft="large">
          <Notice tone="info">
            <Text>{t('Specific position access information')}</Text>
          </Notice>
        </Box>
      )}
      <Checkbox
        id="hasAnalytics"
        checked={hasAnalytics.value}
        label={permissionTrans('Manager reports')}
        description={permissionTrans('hasAnalytics permission description')}
        onChange={hasAnalytics.onChange}
      />
      {accountHasPremiumTalentSearch && (
        <Checkbox
          id="hasPremiumTalentSearch"
          checked={hasPremiumTalentSearch.value}
          label={permissionTrans('Premium Talent Search')}
          description={permissionTrans(
            'hasPremiumTalentSearch permission description',
          )}
          onChange={hasPremiumTalentSearch.onChange}
          disabled={disableSelectPermissions}
        />
      )}
    </Stack>
  );
};
